import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { analysisApi } from '../api/analysis.api';
import type { AnalysisTabs } from '../types/common.types';
import { ANALYSIS_QUERY_KEYS } from '../constants/queryKeys';
import { useMockAnalysis } from './useMockAnalysis';
import { useTour } from '@reactour/tour';

export const useAnalysisQueries = (pageIndex: number, toggle: AnalysisTabs) => {
  const { id } = useParams();
  const { isOpen: isTourOpen } = useTour();

  // Mock data
  const {
    resultData: mockResultData,
    isResultLoading: mockIsResultLoading,
    imageData: mockImageData,
    downloadData: mockDownloadData,
    anomalyData: mockAnomalyData,
    mrzData: mockMrzData,
    notesData: mockNotesData,
  } = useMockAnalysis(pageIndex, toggle);

  // Queries
  const { data: resultData, isLoading: isResultLoading } = useQuery({
    queryKey: ANALYSIS_QUERY_KEYS.RESULT(id ?? ''),
    queryFn: () => analysisApi.getResult(id ?? ''),
    enabled: !!id && !isTourOpen,
  });

  const { data: imageData, isLoading: isImageLoading } = useQuery({
    queryKey: ANALYSIS_QUERY_KEYS.IMAGE(id ?? ''),
    queryFn: () => analysisApi.getImage(id ?? ''),
    enabled: !!id && toggle === 'preview' && !isTourOpen,
  });

  const { data: downloadData } = useQuery({
    queryKey: ANALYSIS_QUERY_KEYS.DOWNLOAD(id ?? ''),
    queryFn: () => analysisApi.getDownload(id ?? ''),
    enabled: !!id && toggle === 'preview' && !isTourOpen,
  });

  const { data: anomalyData } = useQuery({
    queryKey: ANALYSIS_QUERY_KEYS.ANOMALY(id ?? '', pageIndex),
    queryFn: () => analysisApi.getAnomaly(id ?? '', pageIndex),
    enabled: !!id && toggle === 'anomaly' && !isTourOpen,
  });

  const { data: mrzData } = useQuery({
    queryKey: ANALYSIS_QUERY_KEYS.MRZ(id ?? '', pageIndex),
    queryFn: () => analysisApi.getMRZ(id ?? '', pageIndex),
    enabled: !!id && toggle === 'mrz' && !isTourOpen,
  });

  const { data: notesData } = useQuery({
    queryKey: ANALYSIS_QUERY_KEYS.NOTES(id ?? ''),
    queryFn: () => analysisApi.getNotes(id ?? ''),
    enabled: !!id && !isTourOpen,
  });

  if (isTourOpen) {
    return {
      // Mock data
      resultData: mockResultData,
      isResultLoading: mockIsResultLoading,
      imageData: mockImageData,
      downloadData: mockDownloadData,
      anomalyData: mockAnomalyData,
      mrzData: mockMrzData,
      notesData: mockNotesData,
    };
  }

  return {
    // Data
    resultData,
    isResultLoading: isResultLoading,
    isImageLoading: isImageLoading,
    imageData,
    downloadData,
    anomalyData,
    mrzData,
    notesData,
  };
};
